<template>
  <div class="video-player">
    <div v-if="!isHideSelect" class="video-player__bar">
      <div class="button-back">
        <button @click="goBack">
          <Back />
        </button>
      </div>
      <div class="button-log" v-show="showRefreshButton">
        <button @click="refresh">
          <Refresh />
        </button>
      </div>
      <div v-if="isSelect && !isHideSelect" class="video-player__select">
        <select class="video-player__select-season" @change="changeSeason" v-model="selectedSeason">
          <option value="" label="Seasons" disabled aria-disabled="true" />
          <option v-for="season in seasons" :key="season" :value="season" :label="`Season ${season}`" />
        </select>
        <select class="video-player__select-season" @change="changeEpisode" v-model="selectedEpisode">
          <option value="" label="Episodes" disabled aria-disabled="true" />
          <option v-for="episode in episodes" :key="episode" :value="episode" :label="`Episode ${episode}`" />
        </select>
      </div>
    </div>

    <div v-if="hasError" class="error-popup">
      <div class="error-popup__content">
        <button class="error-popup__close" @click="goBack">&times;</button>
        <div class="error-popup__icon">!</div>
        <h3>{{ errorMessage }}</h3>
      </div>
    </div>

    <template v-if="!hasError">
      <div class="video-player__wrap" v-if="isMkv()">
        <PlayerMkv
          :linkVideo="videoSrc"
          :textTrack="textTracks"
          :startTime="historyMoviesStartTime"
          @playing="onVideoPlaying"
          @pause="onVideoPause"
          @timeupdate="onVideoTimeUpdate"
          @waiting="onVideoWaiting"
          @stalled="onVideoStalled"
          @error="onVideoError"
        />
      </div>
      <div class="video-player__wrap" v-else>
        <VideoPlayer
          @playing="onVideoPlaying"
          @pause="onVideoPause"
          :startTime="historyMoviesStartTime"
          @timeupdate="onVideoTimeUpdate"
          :linkVideo="videoSrc"
          :textTracks="textTracks"
          @waiting="onVideoWaiting"
          @stalled="onVideoStalled"
          @error="onVideoError"
        />
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

import Back from '@/assets/icons/svg/back.svg';
import Refresh from '@/assets/icons/svg/refresh.svg';
import PlayerMkv from '@/views/Player/PlayerMkv.vue';
import VideoPlayer from '@/views/Player/VideoPlayer.vue';
import { getItem as getLocalstorageItem, removeItem, setItem } from '@/helpers/storage';

const store = useStore();
const router = useRouter();

const isSelect = ref(false);
const isHideSelect = ref(false);
const videoSrc = ref('');
const episodes = ref([]);
const seasons = ref([]);
const selectedEpisode = ref(1);
const selectedSeason = ref(1);
const trackId = ref('');
const timer = ref(null);
const currentTime = ref(0);
const contentInfo = ref(null);
const showRefreshButton = ref(true);
const leadId = ref(undefined);
const guid = ref(undefined);

const linkVideoState = computed(() => store.state.playVideo.linkPlay || {});
const textTracks = computed(() => store.state.playVideo.subtitles || []);
const infoTvShow = computed(() => store.state.playVideo.infoTvShow || []);
const historyMoviesStartTime = computed(() => store.state.playVideo.historyStartTime || 0);

const hasError = computed(() => store.getters.hasError);
const errorMessage = computed(() => store.getters.errorMessage);

const isMkv = () => videoSrc.value?.includes('.mkv');

const fetchWatchHistory = async () => {
  contentInfo.value = getLocalstorageItem('content-info');
  const id = trackId.value;
  const type = contentInfo.value?.type;
  if (id && type) await store.dispatch('getHistoryTime', { type, id });
};

const sendWatchTime = async () => {
  if (!contentInfo.value || !trackId.value) return;
  const { type, episode, season } = contentInfo.value;
  await store.dispatch('updateHistoryTime', {
    type,
    id: trackId.value,
    episode,
    season,
    seconds: currentTime.value,
    leadId: leadId.value,
    guid: guid.value,
  });
};

const getItem = () => {
  const info = getLocalstorageItem('content-info');
  if (!info || !info.trackId || !info.type) {
    router.push('/');
    return;
  }
  const { trackId: id, type, season, episode } = info;
  trackId.value = id;
  fetchWatchHistory();

  const actions = {
    getLinkSportVod: () => store.dispatch('getLinkSportVod', { id }),
    getLinkLive: () => store.dispatch('getLinkLive', { id: `${id}.m3u8` }),
    getLinkMovie: () => store.dispatch('getLinkMovie', { id }),
    getLinkTvShow: () => {
      store.dispatch('getLinkTvShow', { id, season, episode });
      isSelect.value = true;
    },
  };
  if (actions[type]) actions[type]();
};

const goBack = async () => {
  store.commit('clearError');
  await sendWatchTime();
  router.go(-1);
};

const changeSeason = (e) => {
  const val = e.target.value;
  const info = getLocalstorageItem('content-info');
  setItem('content-info', { ...info, season: val });
  selectedSeason.value = val;
};

const changeEpisode = (e) => {
  const val = e.target.value;
  const info = getLocalstorageItem('content-info');
  setItem('content-info', { ...info, episode: val });
  selectedEpisode.value = val;
  store.dispatch('getLinkTvShow', {
    id: trackId.value,
    season: selectedSeason.value,
    episode: val,
  });
};

const onVideoPlaying = () => {
  isHideSelect.value = true;
  showRefreshButton.value = false;
  if (!timer.value) {
    timer.value = setInterval(() => sendWatchTime(), 10 * 60 * 1000);
  }
};

const onVideoPause = () => {
  isHideSelect.value = false;
  showRefreshButton.value = true;
  if (timer.value) {
    clearInterval(timer.value);
    timer.value = null;
  }
};

const onVideoTimeUpdate = (currentSec) => {
  if (typeof currentSec === 'number' && !isNaN(currentSec)) currentTime.value = currentSec;
};

const refresh = async () => {
  if (currentTime.value > historyMoviesStartTime.value) await sendWatchTime();
  getItem();
};

const onVideoWaiting = () => {
  showRefreshButton.value = true;
};
const onVideoStalled = () => {
  showRefreshButton.value = true;
};
const onVideoError = () => {
  showRefreshButton.value = true;
};

const handleBeforeUnload = () => {
  if (!contentInfo.value) return;
  const { type, episode, season } = contentInfo.value;
  setItem('usaved-data', { type, id: trackId.value, episode, season, seconds: currentTime.value });
};

watch(
  linkVideoState,
  (newVal) => {
    if (newVal?.secure_link) videoSrc.value = newVal.secure_link;
  },
  { immediate: true }
);

watch(
  infoTvShow,
  (newVal) => {
    if (!newVal || newVal.length === 0) return;
    const seasonsList = newVal[0]?.meta_episodes || [];
    const currentSeasonIdx = Math.max(0, selectedSeason.value - 1);
    const episodesList = seasonsList[currentSeasonIdx] || [];
    seasons.value = seasonsList.map((_, index) => index + 1);
    episodes.value = episodesList.map((item) => item.episode);
  },
  { deep: true }
);

onMounted(async () => {
  window.addEventListener('beforeunload', handleBeforeUnload);
  const guideUserCred = getLocalstorageItem('guidCredentials');
  leadId.value = guideUserCred?.lead;
  guid.value = guideUserCred?.guid;

  const unsavedData = getLocalstorageItem('usaved-data');
  if (unsavedData) {
    const { type, id, seconds, episode, season } = unsavedData;
    await store.dispatch('updateHistoryTime', { type, id, episode, season, seconds, leadId: leadId.value, guid: guid.value });
    removeItem('usaved-data');
  }
  getItem();
});

onBeforeUnmount(() => {
  sendWatchTime();
  if (timer.value) clearInterval(timer.value);
  window.removeEventListener('beforeunload', handleBeforeUnload);
  store.commit('clearError');
});
</script>

<style lang="scss" scoped>
.video-player {
  background: #000;
  min-height: 100vh;
  position: relative;

  &__wrap {
    height: 100vh;
    width: 100%;
  }

  &__bar {
    display: flex;
    position: absolute;
    top: 20px;
    z-index: 200;
    left: 30px;
  }

  &__select {
    display: flex;
    margin-left: 100px;

    &-season {
      width: 180px;
      border-radius: 4px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.4);
      color: #fff;
      margin-right: 15px;
      min-height: 40px;
      padding: 0 10px;
      outline: none;
      backdrop-filter: blur(5px);

      option {
        color: #000;
        background: #fff;
      }
    }
  }
}

.button-back,
.button-log {
  button {
    cursor: pointer;
    background: transparent;
    border: 0;
    outline: 0;
    padding: 10px;
    display: flex;
    align-items: center;
    transition: transform 0.2s;
    &:hover {
      transform: scale(1.1);
    }
  }
  svg {
    fill: white;
    width: 30px;
    height: 30px;
  }
}

.button-log {
  margin-left: 10px;
}

.error-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 150;
  backdrop-filter: blur(8px);

  &__content {
    background: #181818;
    padding: 50px 30px;
    border-radius: 12px;
    border: 1px solid #333;
    max-width: 400px;
    width: 85%;
    text-align: center;
    position: relative;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.7);
  }

  &__icon {
    width: 50px;
    height: 50px;
    border: 2px solid #ff4444;
    color: #ff4444;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 30px;
    font-weight: bold;
    margin: 0 auto 20px;
  }

  h3 {
    color: #fff;
    font-size: 20px;
    font-weight: 400;
    margin: 0;
    line-height: 1.4;
  }

  &__close {
    position: absolute;
    top: 10px;
    right: 15px;
    background: transparent;
    border: none;
    color: #888;
    font-size: 32px;
    cursor: pointer;
    line-height: 1;
    &:hover {
      color: #fff;
    }
  }
}
</style>
